import React, { useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Template from './template';
import { Label, SmallMoveServiceRegister } from 'components';
import { useAddress, useApiRequest } from 'hooks';
import { PartnerAuthState } from 'stores/PartnerAuthStore';
import { ServiceState, ServiceAction } from 'stores/ServiceStore';
import {
  ServiceSmallMoveState,
  ServiceSmallMoveAction,
} from 'stores/ServiceSmallMoveStore';
import ZimssaConstant, {
  SERVICE_TYPE,
  GUIDE_TYPE,
  UPLOAD_TYPE,
} from 'commons/ZimssaConstant';
import { validateLength, validateInput, getYear } from 'commons/ZimssaHelper';
import { apiSmallMoveTempSave, apiGetTempData } from 'api/ZimssaAPI';
import {
  BUTTON_NEXT,
  ALERT_NUMBER,
  PLACEHOLDER_TRANSPORTATION_LICENSE_NUMBER,
  PLACEHOLDER_SERVICE_REGIST_NUMBER,
  PAGE_SERVICE_SMALLMOVE_REGISTER_TITLE,
  SELECT_DEFAULT_YEAR,
} from 'commons/ZimssaString';
import { SMALLMOVE_CARINFO_REGISTER } from 'router/PageUrl';

function SmallMoveServiceRegisterView(): React.ReactElement {
  const history = useHistory();
  const { partnerIdx } = useContext(PartnerAuthState);

  const {
    introduce,
    startYear,
    partnerServiceImages,
    businessRegistrationNumber,
    businessRegistrationImage,
    businessRegistrationOwner,
    taxClassification,
    familyRelationImage,
    location1,
    location2,
    serviceValid,
  } = useContext(ServiceState);
  const { province, city, getCityCallback } = useAddress(location1 || '');
  const serviceDispatcher = useContext(ServiceAction);
  const smallMoveDispatcher = useContext(ServiceSmallMoveAction);
  const [fetchGetTempDataResult, fetchGetTempDataSet] =
    useApiRequest<any>(apiGetTempData);

  //파트너 서비스, 활동 이미지(업체 로고)
  const serviceImgUploadDir = ZimssaConstant.IMG_DIR.SERVICE.SMALLMOVE.replace(
    '{{partnerIdx}}',
    `${partnerIdx}`
  );
  //파트너 서류 이미지(주선면허, 보험)
  const docImgUploadDir = ZimssaConstant.IMG_DIR.PARTNER.DOC.replace(
    '{{partnerIdx}}',
    `${partnerIdx}`
  );

  const {
    transportationLicenseNumber,
    transportationLicenseImage,
    smallMoveValid,
  } = useContext(ServiceSmallMoveState);

  useEffect(() => {
    const { result, data } = fetchGetTempDataResult;

    if (result === 'success' && data) {
      serviceDispatcher({
        type: 'SET_STATE',
        value: {
          serviceName: data.service.serviceName,
          introduce: data.service.introduce,
          location1: data.service.location1,
          location2: data.service.location2,
          startYear: data.service.startYear,
          partnerServiceImages: data.service.partnerServiceImages,
          businessRegistrationNumber: data.service.businessRegistrationNumber,
          taxClassification: data.service.taxClassification,
          businessRegistrationImage: data.service.businessRegistrationImage,
          businessRegistrationOwner: data.service.businessRegistrationOwner,
          familyRelationImage: data.service.familyRelationImage,
        },
      });

      smallMoveDispatcher({
        type: 'SET_STATE',
        value: {
          transportationLicenseNumber:
            data.smallMove.transportationLicenseNumber,
          transportationLicenseImage: data.smallMove.transportationLicenseImage,
        },
      });
    }
  }, [fetchGetTempDataResult, serviceDispatcher, smallMoveDispatcher]);

  useEffect(() => {
    fetchGetTempDataSet({
      type: 'REQUEST',
      body: ['smallmove'],
    });
  }, [fetchGetTempDataSet]);

  // 활동 사진 이미지 등록
  const onChangeHandlePartnerImagesCallback = useCallback(
    (value: string[]) => {
      serviceDispatcher({
        type: 'partnerServiceImages',
        value: value,
      });
    },
    [serviceDispatcher]
  );

  const onChangeHandlerCallback = useCallback(
    (type: any, value: string) => {
      serviceDispatcher({
        type: type,
        value: value,
      });
    },
    [serviceDispatcher]
  );

  const onChangeCallback = useCallback(
    (e: any) => {
      const { name, value } = e.target;
      let type: any = name;
      serviceDispatcher({
        type: type,
        value: value,
      });
    },
    [serviceDispatcher]
  );

  const onChangeHandlerProvinceCallback = useCallback(
    (type: any, value: string) => {
      serviceDispatcher({
        type: type,
        value: value,
      });
      serviceDispatcher({
        type: 'location2',
        value: '',
      });
      getCityCallback(value);
    },
    [serviceDispatcher, getCityCallback]
  );

  const onClcikNextCallback = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (serviceValid && smallMoveValid) {
        apiSmallMoveTempSave({
          service: {
            // serviceName: serviceName,
            // serviceImage: serviceImage,
            introduce: introduce,
            startYear: startYear,
            partnerServiceImages: partnerServiceImages,
            businessRegistrationNumber: businessRegistrationNumber,
            businessRegistrationImage: businessRegistrationImage,
            businessRegistrationOwner: businessRegistrationOwner,
            taxClassification: taxClassification,
            familyRelationImage: familyRelationImage,
            location1: location1,
            location2: location2,
          },
          smallMove: {
            transportationLicenseNumber: transportationLicenseNumber,
            transportationLicenseImage: transportationLicenseImage,
          },
        }).then((response) => {
          const { result, message } = response;
          if (result === 'success') {
            history.push(SMALLMOVE_CARINFO_REGISTER.path);
          } else {
            message && alert(message);
          }
        });
      }
    },
    [
      history,
      familyRelationImage,
      businessRegistrationImage,
      businessRegistrationNumber,
      businessRegistrationOwner,
      introduce,
      partnerServiceImages,
      location1,
      location2,
      serviceValid,
      smallMoveValid,
      startYear,
      taxClassification,
      transportationLicenseImage,
      transportationLicenseNumber,
    ]
  );

  const smallMoveServiceRegisterProps = {
    introduce: {
      inputName: 'introduce',
      value: introduce,
      onChange: onChangeCallback,
    },
    location1: {
      inputName: 'location1',
      value: location1 ? location1 : '',
      children: province,
      onChangeHandler: onChangeHandlerProvinceCallback,
    },
    location2: {
      inputName: 'location2',
      value: location2 ? location2 : '',
      children: city,
      onChangeHandler: onChangeHandlerCallback,
    },
    startYear: {
      inputName: 'startYear',
      value: startYear ? startYear : 0,
      children: [{ name: SELECT_DEFAULT_YEAR, value: 0 }, ...getYear(100)],
      onChangeHandler: onChangeHandlerCallback,
    },
    //활동 사진
    partnerServiceImages: {
      serviceType: SERVICE_TYPE.SMALL_MOVE,
      type: GUIDE_TYPE.FIELD_PHOTO,
      uploadType: UPLOAD_TYPE.MULTIPLE,
      inputName: 'partnerServiceImages',
      imgDir: serviceImgUploadDir,
      guideMessageList: [
        '휴대전화 번호와 같이 개인정보가 노출되거나 그외 업체 홍보에 부적절한 사진은 심사를 통해 사진 등록이 제한될 수 있습니다.',
        '한 번에 업로드 가능한 최대 이미지의 용량은 10MB이며 JPG, PNG 확장자의 이미지 파일만 업로드 가능합니다.',
        '용량을 초과할 경우 업로드가 제한될 수 있습니다.',
      ],
      onChangeHandler: onChangeHandlePartnerImagesCallback,
    },
    businessRegistrationNumber: {
      inputProps: {
        inputName: 'businessRegistrationNumber',
        placeholder: PLACEHOLDER_SERVICE_REGIST_NUMBER,
        isValid: (() => {
          if (validateInput(businessRegistrationNumber.trim())) {
            if (validateLength(businessRegistrationNumber, 10)) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        })(),
        value: businessRegistrationNumber,
      },
      caption:
        validateInput(businessRegistrationNumber.trim()) &&
        !validateLength(businessRegistrationNumber, 10) ? (
          <Label
            contents={ALERT_NUMBER}
            color={'red_900'}
            fontStyle={'caption'}
          />
        ) : null,
      onChangeHandler: onChangeHandlerCallback,
    },
    taxClassification: {
      state: taxClassification,
      onClick: useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>, value?: any) => {
          serviceDispatcher({
            type: 'taxClassification',
            value,
          });
        },
        [serviceDispatcher]
      ),
    },
    //사업자 등록증 사진
    businessRegistrationImage: {
      inputName: 'businessRegistrationImage',
      imgDir: docImgUploadDir,
      isDeleteBtn: true,
      value: businessRegistrationImage,
      onChangeHandler: onChangeHandlerCallback,
    },
    businessRegistrationOwner: {
      state: businessRegistrationOwner,
      onClick: useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>, value?: any) => {
          serviceDispatcher({
            type: 'businessRegistrationOwner',
            value: value,
          });

          if (value !== '가족') {
            serviceDispatcher({
              type: 'familyRelationImage',
              value: '',
            });
          }
        },
        [serviceDispatcher]
      ),
    },
    //가족관계 증명서
    familyRelationImage: {
      inputName: 'familyRelationImage',
      imgDir: docImgUploadDir,
      isDeleteBtn: true,
      value: familyRelationImage,
      guideMessageList: [
        '사업자 명의가 가족일 경우 가족관계증명서도 함께 업로드 부탁드립니다.',
      ],
      onChangeHandler: onChangeHandlerCallback,
    },
    transportationLicenseNumber: {
      inputProps: {
        inputName: 'transportationLicenseNumber',
        placeholder: PLACEHOLDER_TRANSPORTATION_LICENSE_NUMBER,
        isValid: (() => {
          if (validateInput(transportationLicenseNumber.trim())) {
            if (validateLength(transportationLicenseNumber, 9)) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        })(),
        value: transportationLicenseNumber,
      },
      caption:
        validateInput(transportationLicenseNumber.trim()) &&
        !validateLength(transportationLicenseNumber, 9) ? (
          <Label
            contents={ALERT_NUMBER}
            color={'red_900'}
            fontStyle={'caption'}
          />
        ) : null,
      onChangeHandler: (type: any, value: string) => {
        smallMoveDispatcher({
          type,
          value,
        });
      },
    },
    //화물운송종사자 자격증
    transportationLicenseImage: {
      inputName: 'transportationLicenseImage',
      imgDir: docImgUploadDir,
      isDeleteBtn: true,
      value: transportationLicenseImage,
      onChangeHandler: (type: any, value: string) => {
        smallMoveDispatcher({
          type,
          value,
        });
      },
    },
    btnNext: {
      contents: BUTTON_NEXT,
      isActive: (serviceValid || false) && (smallMoveValid || false),
      isComplete: false,
      onClick: onClcikNextCallback,
    },
  };

  return (
    <Template title={PAGE_SERVICE_SMALLMOVE_REGISTER_TITLE}>
      <SmallMoveServiceRegister {...smallMoveServiceRegisterProps} />
    </Template>
  );
}

export default SmallMoveServiceRegisterView;
